import { Component, Mixins } from 'vue-property-decorator'
import io from 'socket.io-client'
import { SOCKET_AUTHORIZATION_SERVICE_URL, CLIENT } from '@/constants'
import * as AUTHORIZATION from '@/@next/store/authorization'
import { Action, Getter, Mutation } from 'vuex-class'
import { Lock, Maybe } from '@/@types/graphql'
import { authorization, TicketStatus } from '@/@next/store/authorization'
import { SettingsMixin } from '@/@next/mixins'

@Component({ name: 'authorization-mixin' })
export default class Authorization extends Mixins(SettingsMixin) {

  @Getter(AUTHORIZATION.GET_USER_AUTHORIZATIONS)
  getCurrentAuthorizations!: string[]
  @Action(AUTHORIZATION.SET_TICKET_INPUT)
  setTicketInput!: (lock: Lock) => void
  @Mutation(AUTHORIZATION.SET_SHOW_TICKET_INPUT)
  setShowTicketInput!: (show: boolean) => void
  @Getter(AUTHORIZATION.SHOW_TICKET_INPUT)
  showTicketInput!: boolean
  @Action(AUTHORIZATION.ADD_AUTHORIZATIONS)
  addAuthorizations!: (authorizationArray: string[]) => void
  @Action(AUTHORIZATION.REMOVE_AUTHORIZATIONS)
  removeAuthorizations!: (authorizationArray: string[]) => void
  @Mutation(AUTHORIZATION.SET_EXTERNAL_AUTHORIZATION_SOCKET)
  setExternalAuthServiceSocket!: (socket: any) => void
  @Getter(AUTHORIZATION.GET_EXTERNAL_AUTHORIZATION_SOCKET)
  getExternalAuthServiceSocket!: any
  @Action(AUTHORIZATION.HAS_AUTHORIZATIONS)
  hasAuthorizations!: (authorizations: string[]) => boolean
  @Getter(AUTHORIZATION.GET_CURRENT_LOCK)
  getCurrentLock!: Maybe<Lock>
  @Action(AUTHORIZATION.SET_CURRENT_LOCK)
  setCurrentLock!: (lock: Lock) => void
  @Getter(AUTHORIZATION.GET_USER_ID)
  getUserId!: string | null
  @Action(AUTHORIZATION.SET_TICKET_STATUS)
  setTicketStatus!: (status: TicketStatus) => void
  @Mutation(AUTHORIZATION.SET_TICKET_DATA)
  setTicketData!: (ticket: any) => void

  authorizationCookieName = 'WIVI_AUTHORIZATION'
  ticketCookieName = 'WIVI_TICKET'

  socket: any
  authorizationCookie: string[] = []
  isAuthorized: boolean = false
  
  hasAccessControl: boolean = false
  userId: string | null = null
  ticket: string | null = null
  hasTicketQuery: boolean = false

  get hasSocketAuthorizationService () {
    return !!SOCKET_AUTHORIZATION_SERVICE_URL
  }
  
  async initAuthorization () {
    
    // Get ticket Cookie
    const ticketCookie = this.getTicketCookie()
    if (ticketCookie && typeof ticketCookie === 'string') this.ticket = ticketCookie
    // Get ticketQuery
    const { ticket, ...queries } = this.$route.query
    if (ticket && typeof ticket === 'string') {
      this.ticket = ticket
      this.$router.replace({ query: queries })
      this.hasTicketQuery = true
    }

    if (this.ticket) this.saveTicket(this.ticket)
    // Get Authorization Coockie
    this.authorizationCookie = this.getAuthorizationCookie()
    this.addAuthorizations(this.authorizationCookie)

    const hasSettingsTicketAuth = this.checkSettingsTicketsAuthorization()
    if (!hasSettingsTicketAuth && this.ticket) this.connectToExternalAuthorizationServiceSocket()

  }

  checkSettingsTicketsAuthorization () {
    const settingsTicket = this._getSettingsTickets?.find((ticket: any) => ticket.ticketNumber === this.ticket)
    if (settingsTicket) {
      console.info('[Info][Authorization][dataset] Ticket is in dataset', settingsTicket)
      if (!Array.isArray(settingsTicket.authorizations)) return false
      // TODO: check date
      const now = new Date()
      console.log('Test ', now < new Date(settingsTicket.startAt), now > new Date(settingsTicket.stopAt))
      if (now < new Date(settingsTicket.startAt)) {
        console.info('[Info][Authorization][dataset] Ticket is not started yet')
        this.setTicketStatus(TicketStatus.tooEarly)
        return true
      } else if (now > new Date(settingsTicket.stopAt)) {
        console.info('[Info][Authorization][dataset] Ticket is expired')
        this.setTicketStatus(TicketStatus.tooLate)
        return true
      }
      this.addAuthorizations(settingsTicket.authorizations)
      this.setAuthCookie()
      this.setTicketStatus(TicketStatus.valid)
      return true
    }
    return false
  }

  connectToExternalAuthorizationServiceSocket () {
    return new Promise((resolve, reject) => {
      try {
        // Has Socket authorization service for this project ?
        if (!this.hasSocketAuthorizationService || !SOCKET_AUTHORIZATION_SERVICE_URL) return resolve(null)

        // Open Access Socket
        this.socket = io.connect(SOCKET_AUTHORIZATION_SERVICE_URL)

        // Socket disconnection
        this.socket.on('disconnect', () => {
          console.info('[Info][Authorization][Socket] client disconnect')
          this.socket = null
          // this.removeAuthorizations(this.getCurrentAuthorizations)
          // this.setDeleteAuthCookieTimeout()
        })

        // Receive authorization from socket
        this.socket.on('ticket_authorization', (response: any) => {
          console.info('[Info][Authorization][Socket] client receive authorization ', response)
          if (response.isAuthorized) this.addAuthorizations(response.authorizations)
          else this.removeAuthorizations(response.authorizations)
          this.setAuthCookie()
          // TODO Faire une petite verification sur le ticket suatus si hors énumeration
          if (!response.ticketStatus) return this.setTicketStatus(TicketStatus.error)
          if (Object.values(TicketStatus).includes(response.ticketStatus)) {
            this.setTicketStatus(response.ticketStatus)
          } else {
            response.isAuthorized ? this.setTicketStatus(TicketStatus.valid) : this.setTicketStatus(TicketStatus.error)
          }
          this.setTicketData(response?.ticketData || {})
          if (!response.isAuthorized && this.hasTicketQuery) this.setShowTicketInput(true)
        })
        
        // On socket connection
        this.socket.on('connect', () => {
          console.info('[Info][Authorization][Socket] client connect')
          if (this.ticket) this.sendUserInfo(
            [{
              type: 'ticketNumber',
              value: this.ticket
            }]
          )
          else this.sendUserInfo()
          resolve(this.socket)
        })
      } catch (error) {
        console.error(`[Error][Authorization][Socket] Error during connection to external redirection service ${error}`)
        reject(error)
      }
    })
  }
  
  getAuthorizationCookie (): string[] {
    const auth = this.$cookies.get(this.authorizationCookieName)  
    if (!auth) return []
    const arr = JSON.parse(auth)
    if (!arr.isArray) return []
    return arr
  }

  getTicketCookie (): string | null {
    const ticket = this.$cookies.get(this.ticketCookieName)
    return ticket || null
  }

  setAuthCookie () {
    this.$cookies.set(this.authorizationCookieName, JSON.stringify(this.getCurrentAuthorizations))
  }

  saveTicket (value: string) {
    // Update Ticket in react native webview context 
    // @ts-ignore
    if (window.ReactNativeWebView && window.ReactNativeWebView.postMessage) {
      // @ts-ignore
      window.ReactNativeWebView.postMessage(JSON.stringify({
        type: 'SET_TICKET',
        value
      }))
    }
    this.setTicketCookie(value)
  }

  setTicketCookie (value: string) {
    this.$cookies.set(this.ticketCookieName, value)
    if (value) this.ticket = value
  }

  deleteAuthCookie () {
    this.$cookies.remove(this.authorizationCookieName)
  }

  deleteTicketCookie () {
    this.$cookies.remove(this.ticketCookieName)
  }

  async sendUserInfo (data: any = []) {
    const userId: Maybe<string> = this.getUserId
    if (!userId) {
      return console.error('[Info][Authorization][User] Send user info needs userId')
    }
    
    const userInfo = {
      clientId: CLIENT,
      userId,
      authorizations: this.getCurrentAuthorizations,
      data
    }
    this.socket.send('user-info', userInfo)
  }

  setDeleteAuthCookieTimeout () {
    // TODO: Set Timeout to delete auth Coockie
  }

  async sendTicket (value: string) {
    const tiket = {
      type: 'ticketNumber',
      value
    }
    this.saveTicket(value)
    // const hasSettingsTicketAuth = this.checkSettingsTicketsAuthorization()
    if (!this.checkSettingsTicketsAuthorization()) {
      if (this.socket) return this.sendUserInfo([tiket])
      const socket = await this.connectToExternalAuthorizationServiceSocket()
      if (!socket) return this.setTicketStatus(TicketStatus.notExist)
      return this.sendUserInfo([tiket])
    } else if (this.socket) this.socket.close()
    // this.socket.send('user-info', { userId: this.userId, isAuthorized: this.isAuthorized, ticket: value })
    
  }

  isRessourceAuhtorized (locks: Lock[]) {
    const lockSlugs = locks.map((a: Lock) => a.slug)
    const unlockSlugs = this.getCurrentAuthorizations.filter((s: string) => lockSlugs.includes(s))
    return unlockSlugs.length === lockSlugs.length
  }

  unlock (locks: Lock[]) {
    console.info('[Info][Authorization] unlock authorization mixin', locks)
    // Show différents page defined on locks
    // On va considérer ici qu'il n'y a qu'un vérrou de type ticket
    const ticketLock = locks[0]
    // If only ticket input
    this.setTicketInput(ticketLock)
  }

}
